export const EXPECTED_GOALS_FILTER_MAX_LABEL = "MAX";
export const EXPECTED_GOALS_SLIDER_MIN = 0;
export const EXPECTED_GOALS_SLIDER_MAX = 5;

/**
 * Discrete raw-value ticks for advanced-metric sliders (indexes 0–5).
 * Bounds follow the quintile distribution (roughly 40th / 60th / 80th / high-position 80th) plus an open MAX.
 */
export const EXPECTED_GOALS_FILTER_VALUES = {
	totalXg: [0, 0.05, 0.15, 0.3, 0.5, EXPECTED_GOALS_FILTER_MAX_LABEL],
	nonPenaltyXg: [0, 0.05, 0.15, 0.3, 0.5, EXPECTED_GOALS_FILTER_MAX_LABEL],
	totalXa: [0, 0.03, 0.1, 0.2, 0.3, EXPECTED_GOALS_FILTER_MAX_LABEL],
	nonPenaltyXgPerShot: [0, 0.05, 0.08, 0.12, 0.2, EXPECTED_GOALS_FILTER_MAX_LABEL],
	nonPenaltyXgPlusXaPer90: [0, 0.05, 0.2, 0.4, 0.7, EXPECTED_GOALS_FILTER_MAX_LABEL],
	goalsXgRatio: [0, 0.5, 0.8, 1, 1.5, EXPECTED_GOALS_FILTER_MAX_LABEL],
} as const;

export type ExpectedGoalsFilterKey = keyof typeof EXPECTED_GOALS_FILTER_VALUES;

export const EXPECTED_GOALS_FILTER_KEYS = Object.keys(EXPECTED_GOALS_FILTER_VALUES) as ExpectedGoalsFilterKey[];

export const EXPECTED_GOALS_FILTER_KEY_SET = new Set<string>(EXPECTED_GOALS_FILTER_KEYS);

export function isExpectedGoalsFilterKey(key: string): key is ExpectedGoalsFilterKey {
	return EXPECTED_GOALS_FILTER_KEY_SET.has(key);
}

export function getExpectedGoalsSliderMapping(key: ExpectedGoalsFilterKey): Record<number, number | string> {
	const values = EXPECTED_GOALS_FILTER_VALUES[key];
	return {
		0: values[0],
		1: values[1],
		2: values[2],
		3: values[3],
		4: values[4],
		5: values[5],
	};
}

export function getExpectedGoalsFilterRange(
	key: ExpectedGoalsFilterKey,
	from: number,
	to: number,
): { gte?: number; lte?: number } {
	const values = EXPECTED_GOALS_FILTER_VALUES[key];
	const mappedFrom = values[from];
	const mappedTo = values[to];

	if (from === EXPECTED_GOALS_SLIDER_MIN && to === EXPECTED_GOALS_SLIDER_MAX) {
		return {};
	}
	if (from === EXPECTED_GOALS_SLIDER_MIN && to !== EXPECTED_GOALS_SLIDER_MAX && typeof mappedTo === "number") {
		return { lte: mappedTo };
	}
	if (to === EXPECTED_GOALS_SLIDER_MAX && from !== EXPECTED_GOALS_SLIDER_MIN && typeof mappedFrom === "number") {
		return { gte: mappedFrom };
	}
	if (typeof mappedFrom === "number" && typeof mappedTo === "number") {
		return { gte: mappedFrom, lte: mappedTo };
	}
	if (typeof mappedFrom === "number") {
		return { gte: mappedFrom };
	}
	if (typeof mappedTo === "number") {
		return { lte: mappedTo };
	}
	return {};
}

export function expectedGoalsValueToIndex(
	key: ExpectedGoalsFilterKey,
	value: number | string | null,
	bound: "lower" | "upper",
): number {
	if (value === null || value === undefined || value === EXPECTED_GOALS_FILTER_MAX_LABEL) {
		return bound === "lower" ? EXPECTED_GOALS_SLIDER_MIN : EXPECTED_GOALS_SLIDER_MAX;
	}

	const numericValue = typeof value === "number" ? value : Number(value);
	if (Number.isFinite(numericValue) === false) {
		return bound === "lower" ? EXPECTED_GOALS_SLIDER_MIN : EXPECTED_GOALS_SLIDER_MAX;
	}

	const values = EXPECTED_GOALS_FILTER_VALUES[key];
	let closestIndex = bound === "lower" ? EXPECTED_GOALS_SLIDER_MIN : EXPECTED_GOALS_SLIDER_MAX;
	let closestDistance = Number.POSITIVE_INFINITY;

	for (let index = EXPECTED_GOALS_SLIDER_MIN; index < EXPECTED_GOALS_SLIDER_MAX; index += 1) {
		const mapped = values[index];
		if (typeof mapped !== "number") {
			continue;
		}
		const distance = Math.abs(mapped - numericValue);
		if (distance < closestDistance) {
			closestDistance = distance;
			closestIndex = index;
		}
	}

	return closestIndex;
}

export function expectedGoalsApiRangeToTicks(
	key: ExpectedGoalsFilterKey,
	lowerValue: number | string | null,
	upperValue: number | string | null,
): [number, number] {
	return [
		expectedGoalsValueToIndex(key, lowerValue, "lower"),
		expectedGoalsValueToIndex(key, upperValue, "upper"),
	];
}
